import React from 'react';
import { motion } from 'framer-motion';
import { Box, Paper, Typography, Chip, Button, LinearProgress, Stack, Divider, Tooltip } from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

/**
 * VersionScheduleCard — one project version as an enrollment "batch" card.
 *
 * Pure presentational: everything it shows comes from the ngb_versions row +
 * the server-derived `state` object (displayStatus, seats, canEnroll,
 * isAutoAssignedVersion, reasonIfCannotEnroll, startsInDays…). The card never
 * decides enrollability itself — the server already did.
 */

// Status → color system (spec: Upcoming Blue, Open Green, Almost Full Orange,
// Full Red, Running Purple, Completed Gray, Cancelled Dark Gray)
const STATUS_STYLES = {
  'Upcoming':     { bg: '#DBEAFE', fg: '#1D4ED8', bar: '#3B82F6' },
  'Open':         { bg: '#DCFCE7', fg: '#166534', bar: '#22C55E' },
  'Closing Soon': { bg: '#FEF3C7', fg: '#92400E', bar: '#F59E0B' },
  'Almost Full':  { bg: '#FFEDD5', fg: '#C2410C', bar: '#F97316' },
  'Full':         { bg: '#FEE2E2', fg: '#991B1B', bar: '#EF4444' },
  'Running':      { bg: '#EDE9FE', fg: '#5B21B6', bar: '#8B5CF6' },
  'Completed':    { bg: '#F1F5F9', fg: '#64748B', bar: '#9CA3AF' },
  'Cancelled':    { bg: '#E5E7EB', fg: '#374151', bar: '#4B5563' },
  'Closed':       { bg: '#F1F5F9', fg: '#475569', bar: '#94A3B8' }
};

const styleFor = (status) => STATUS_STYLES[status] || STATUS_STYLES.Closed;

const fmtDate = (value) => {
  if (!value) return 'TBD';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'TBD';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const DateRow = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      {React.cloneElement(icon, { sx: { fontSize: 15, color: '#94A3B8' } })}
      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</Typography>
    </Box>
    <Typography variant="caption" sx={{ color: '#1E293B', fontWeight: 700, textAlign: 'right' }}>{value}</Typography>
  </Box>
);

const VersionScheduleCard = ({ version, enrolledVersionId, onEnroll, enrolling, disabled }) => {
  const v = version || {};
  const state = v.state || {};
  const status = state.displayStatus || (v.enrollment_open ? 'Open' : 'Upcoming');
  const sx = styleFor(status);

  const seatsFilled = state.seatsFilled ?? (parseInt(v.members_enrolled, 10) || 0);
  const maxSeats = state.maxSeats ?? Math.min(parseInt(v.team_size, 10) || 25, 25);
  const seatsRemaining = state.seatsRemaining ?? Math.max(0, maxSeats - seatsFilled);
  const fillPct = maxSeats > 0 ? Math.min((seatsFilled / maxSeats) * 100, 100) : 0;

  const isEnrolledHere = enrolledVersionId != null && String(enrolledVersionId) === String(v.id);
  const isAutoTarget = state.isAutoAssignedVersion === true;
  const startsInDays = state.startsInDays;

  // Action button per spec: Enroll Here / Auto Assigned / Already Enrolled /
  // Full / Coming Soon — disabled unless this is the enrollable target.
  let buttonLabel;
  let buttonEnabled = false;
  if (isEnrolledHere) {
    buttonLabel = 'Already Enrolled';
  } else if (state.canEnroll && isAutoTarget) {
    buttonLabel = 'Enroll Here';
    buttonEnabled = true;
  } else if (state.canEnroll) {
    // Enrollable in principle, but auto-assignment targets an earlier version
    buttonLabel = 'Auto Assigned Later';
  } else if (status === 'Full') {
    buttonLabel = 'Full';
  } else if (status === 'Upcoming') {
    buttonLabel = 'Coming Soon';
  } else if (status === 'Running') {
    buttonLabel = 'In Progress';
  } else if (status === 'Completed') {
    buttonLabel = 'Completed';
  } else if (status === 'Cancelled') {
    buttonLabel = 'Cancelled';
  } else {
    buttonLabel = 'Closed';
  }

  return (
    <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.2 }} style={{ height: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5, borderRadius: '20px', height: '100%', display: 'flex', flexDirection: 'column',
          border: '2px solid',
          borderColor: isEnrolledHere ? '#27AE60' : isAutoTarget ? '#6C3BFF' : '#E2E8F0',
          bgcolor: 'white', position: 'relative', overflow: 'visible',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)' }
        }}
      >
        {(isAutoTarget || isEnrolledHere) && (
          <Box sx={{ position: 'absolute', top: -12, right: 16, zIndex: 2 }}>
            <Chip
              icon={isEnrolledHere
                ? <CheckCircleRoundedIcon sx={{ fontSize: '14px !important', color: 'white !important' }} />
                : <BoltRoundedIcon sx={{ fontSize: '14px !important', color: 'white !important' }} />}
              label={isEnrolledHere ? 'Your Batch' : 'Auto Assigned'}
              size="small"
              sx={{
                bgcolor: isEnrolledHere ? '#27AE60' : '#6C3BFF', color: 'white',
                fontWeight: 800, border: '2px solid white', fontSize: '10px'
              }}
            />
          </Box>
        )}

        {/* Header: name + status badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>{v.name}</Typography>
          <Chip label={status === 'Open' ? 'Enrollment Open' : status} size="small"
            sx={{ bgcolor: sx.bg, color: sx.fg, fontWeight: 800, fontSize: '10px', height: 22 }} />
        </Box>

        {/* Countdown */}
        {startsInDays != null && startsInDays > 0 && status !== 'Completed' && status !== 'Cancelled' && (
          <Typography variant="caption" sx={{ color: sx.fg, fontWeight: 700, mb: 1 }}>
            Starts in {startsInDays} day{startsInDays === 1 ? '' : 's'}
          </Typography>
        )}

        {/* Optional short description */}
        {v.description && (
          <Typography variant="caption" sx={{
            color: '#64748B', mb: 1.5, display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
          }}>
            {v.description}
          </Typography>
        )}

        {/* Seats + progress bar */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <GroupsRoundedIcon sx={{ fontSize: 15, color: '#94A3B8' }} /> {seatsFilled} / {maxSeats} enrolled
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 800, color: seatsRemaining === 0 ? '#DC2626' : '#16A34A' }}>
              {seatsRemaining === 0 ? 'No seats left' : `${seatsRemaining} seat${seatsRemaining === 1 ? '' : 's'} left`}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={fillPct}
            sx={{
              height: 8, borderRadius: 4, bgcolor: '#F1F5F9',
              '& .MuiLinearProgress-bar': { bgcolor: sx.bar, borderRadius: 4 }
            }}
          />
        </Box>

        <Divider sx={{ borderStyle: 'dashed', mb: 1.5 }} />

        {/* Schedule */}
        <Stack spacing={0.75} sx={{ mb: 1.5, flexGrow: 1 }}>
          <DateRow icon={<CalendarMonthRoundedIcon />} label="Starts" value={fmtDate(v.start_date)} />
          <DateRow icon={<CalendarMonthRoundedIcon />} label="Ends" value={fmtDate(v.end_date)} />
          <DateRow icon={<EventAvailableRoundedIcon />} label="Enrollment opens" value={fmtDate(v.enrollment_opens)} />
          <DateRow icon={<EventBusyRoundedIcon />} label="Enrollment closes" value={fmtDate(v.enrollment_closes)} />
          {v.kickoff_date && (
            <DateRow icon={<RocketLaunchRoundedIcon />} label="Kickoff" value={fmtDate(v.kickoff_date)} />
          )}
          {v.sprint_duration && (
            <DateRow icon={<AccessTimeRoundedIcon />} label="Sprint cadence" value={v.sprint_duration} />
          )}
          {(v.repository_url) && (
            <DateRow icon={<CodeRoundedIcon />} label="Repository" value="Ready" />
          )}
        </Stack>

        {/* Action */}
        <Tooltip title={!buttonEnabled && state.reasonIfCannotEnroll ? state.reasonIfCannotEnroll : ''} arrow>
          <span>
            <Button
              fullWidth
              variant={buttonEnabled || isEnrolledHere ? 'contained' : 'outlined'}
              disabled={!buttonEnabled || Boolean(disabled) || Boolean(enrolling)}
              onClick={buttonEnabled && onEnroll ? onEnroll : undefined}
              sx={{
                borderRadius: '12px', textTransform: 'none', fontWeight: 800,
                bgcolor: isEnrolledHere ? '#27AE60' : buttonEnabled ? '#6C3BFF' : 'transparent',
                borderColor: '#CBD5E1',
                color: isEnrolledHere || buttonEnabled ? 'white' : '#94A3B8',
                '&:hover': { bgcolor: buttonEnabled ? '#5A2EE5' : undefined },
                '&.Mui-disabled': {
                  bgcolor: isEnrolledHere ? '#27AE60' : 'transparent',
                  color: isEnrolledHere ? 'white' : '#94A3B8'
                }
              }}
            >
              {enrolling && buttonEnabled ? 'Enrolling…' : buttonLabel}
            </Button>
          </span>
        </Tooltip>
      </Paper>
    </motion.div>
  );
};

export default VersionScheduleCard;
